
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import {
  Lead, StepNames, Task, User,
} from '@/types';
import { leadRouteCode } from '@/helpers/leadCode';

@Component
export default class LeadTaskStatus extends Vue {
  @Prop(Object) readonly task: Task | undefined;

  @Prop(String) readonly step: StepNames | undefined;

  @Prop({ type: Boolean, default: false }) readonly first!: boolean;

  @Prop({ type: Boolean, default: false }) readonly last!: boolean;

  get lead(): Lead | null {
    return this.$store.getters.currentLead;
  }

  get user(): User | null {
    return this.$store.getters.currentUser;
  }

  get isSuperUser(): boolean {
    return (this.user && this.user.isSuperUser) ?? false;
  }

  get isCurrentProject(): boolean {
    return Boolean(
      this.lead && this.task && this.lead.id === (this.task.childLeadId ?? this.task.LeadId),
    );
  }

  get childLeads() {
    // return this.$store.getters.currentChildren;
    return this.$store.getters.currentChildrenInfo;
  }

  get leadId(): number | null {
    if (this.task?.childLeadId && this.task.childLeadId) return this.task.childLeadId;

    // if (this.lead && this.lead.id) return this.lead.id;

    return this.task?.LeadId || null;
  }

  get leadInfo(): Lead | null {
    if (this.leadId
      && this.leadId in this.childLeads) return this.childLeads[this.leadId];

    if (this.leadId && this.lead
      && this.lead.id === this.leadId) return this.lead;

    return null;
  }

  get canShow() {
    if (this.isCurrentProject) return true;

    if (this.leadId === this.task?.LeadId) return true;

    if (!this.leadInfo || !this.user) return false;

    const allowed = this.isSuperUser
      || (this.leadInfo as any).allowedUsers.includes(`:${this.user.id}:`);

    return allowed;
  }

  get currentLeadStatus() {
    return this.isCurrentProject
      ? this.lead?.status
      : this.leadInfo?.status;
  }

  get taskClass() {
    let classes = '';

    if (this.first) {
      classes += 'start ';
    } else if (this.last) {
      classes += 'end ';
    } else {
      classes += 'middle ';
    }

    if (this.task) {
      classes += `task-${this.task.name} `;
      if (this.currentLeadStatus === 'resolved' && !this.task.status) {
        classes += 'lts-green soft ';
      } else
      if (!this.task.status) {
        classes += 'lts-lightgrey ';
      } else
      if (this.task.status === 'disabled') {
        classes += 'lts-darkgrey ';
      } else
      if (this.task.status === 'completed') {
        classes += this.task.extraCosti ? 'lts-yellow ' : 'lts-green ';
      } else
      if (this.task.status === 'error') {
        classes += 'lts-red ';
      } else {
        classes += 'lts-lightgrey ';
      }
    } else
    if (this.step && this.lead && this.step in this.lead) {
      switch (this.lead[this.step]) {
        case true:
          classes += 'lts-green ';
          break;
        case false:
          classes += 'lts-red ';
          break;
        default:
          classes += 'lts-lightgrey ';
      }
    } else {
      classes += 'lts-lightgrey ';
    }

    return classes;
  }

  get image() {
    let name = '';

    if (this.first) {
      name += 'start-';
    } else if (this.last) {
      name += 'end-';
    } else {
      name += 'middle-';
    }

    if (this.task) {
      if (this.currentLeadStatus === 'resolved' && !this.task.status) {
        name += 'green';
      } else
      if (!this.task.status) {
        name += 'lightgrey';
      } else
      if (this.task.status === 'disabled') {
        name += 'darkgrey';
      } else
      if (this.task.status === 'completed') {
        name += this.task.extraCosti ? 'yellow' : 'green';
      } else
      if (this.task.status === 'error') {
        name += 'red';
      } else {
        name += 'lightgrey';
      }
    } else
    if (this.step && this.lead && this.step in this.lead) {
      switch (this.lead[this.step]) {
        case true:
          name += 'green';
          break;
        case false:
          name += 'red';
          break;
        default:
          name += 'lightgrey';
      }
    } else {
      name += 'lightgrey';
    }

    name += '.png';

    return name;
  }

  get height() {
    return '24px';
  }

  get width() {
    if (this.first || this.last) {
      return '30px';
    }
    return '36px'; // Middle
  }

  get label() {
    if (this.task) {
      return this.task.name;
    }

    if (this.step) {
      switch (this.step) {
        case 'step1':
          return 'C';
        case 'step2':
          return 'S';
        case 'step3':
          return 'P';
        case 'step4':
          return 'A';

        default:
          return '?';
      }
    }

    return '?';
  }

  get title() {
    if (this.task) {
      let text = '';
      if (this.task.relationship === 'father') {
        text += `${this.task.childName || 'Lead figlia'}: Task ${this.task.name}`;

        switch (this.task.status) {
          case 'completed':
            text += ' completato';
            break;
          case 'error':
            text += ' fallito';
            break;
          case 'disabled':
            text += ' disabilitato';
            break;
          default:
            break;
        }
      } else {
        text += `${this.task.text}`;
      }

      if (this.task.name === 'A' && this.task.extraCosti) {
        text += ' (con extra-costi)';
      }

      return text;
    }

    if (this.step && this.lead && this.step in this.lead) {
      switch (this.step) {
        case 'step1':
          if (this.lead[this.step] === null) return 'Cliente da contattare';
          return this.lead[this.step] === true
            ? 'Cliente contattato'
            : 'Cliente non contattato';
        case 'step2':
          if (this.lead[this.step] === null) return 'Sopralluogo da fissare';
          return this.lead[this.step] === true
            ? 'Sopralluogo fissato'
            : 'Sopralluogo non fissato';
        case 'step3':
          if (this.lead[this.step] === null) return 'Preventivo da consegnare';
          return this.lead[this.step] === true
            ? 'Preventivo consegnato'
            : 'Preventivo non consegnato';
        case 'step4':
          if (this.lead[this.step] === null) return 'Accettazione non ancora ricevuta';
          return this.lead[this.step] === true
            ? 'Accettazione ricevuta'
            : 'Preventivo rifiutato';

        default:
          return '?';
      }
    }

    return 'Tooltip di esempio';
  }

  onShowLead() {
    console.log(`${this.constructor.name}: onShowLead()`); // eslint-disable-line no-console

    const id = this.task?.childLeadId ?? this.task?.LeadId;

    this.$router.push({ name: 'Lead', params: { code: leadRouteCode(`${id}`) } });
    // this.$router.push({
    //   name: 'Lead', params: { code: leadRouteCode(`${id}`) }, query: { tab: 'dashboard' },
    // });
  }
}
